import React, { useState, useEffect } from 'react';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { useNavigate } from 'react-router-dom';
import { FiFilter } from 'react-icons/fi';

// Add this constant back
const ITEMS_PER_PAGE = 15;

// Add this helper component for tooltip cells
function TooltipCell({ content, className, children, align = 'center' }) {
  return (
    <td 
      className={`px-6 py-4 text-sm truncate relative group text-${align} ${className}`}
      title={content}
    >
      <span className="truncate block">
        {children || content}
      </span>
      <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded-md text-xs whitespace-normal max-w-xs -translate-y-full top-0 left-1/2 transform -translate-x-1/2">
        {content}
      </div>
    </td>
  );
}

// Update the ColumnFilterDropdown component
const ColumnFilterDropdown = ({ isOpen, options, selectedValues, onSelect, onClose }) => {
  const [tempSelected, setTempSelected] = useState([...selectedValues]);

  if (!isOpen) return null;

  // Handle temporary selection
  const handleTempSelect = (value) => {
    setTempSelected(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      return [...prev, value];
    });
  };

  // Handle Select All
  const handleSelectAll = () => {
    if (tempSelected.length === options.length) {
      setTempSelected([]);
    } else {
      setTempSelected([...options]);
    }
  };

  // Handle Apply
  const handleApply = () => {
    // Update parent state with all changes at once
    tempSelected.forEach(value => {
      if (!selectedValues.includes(value)) {
        onSelect(value);
      }
    });
    selectedValues.forEach(value => {
      if (!tempSelected.includes(value)) {
        onSelect(value);
      }
    });
    onClose();
  };

  // Handle Clear
  const handleClear = () => {
    // Clear the temporary selection state
    setTempSelected([]);
    // Clear all selections in parent component
    selectedValues.forEach(value => {
      onSelect(value);
    });
    onClose();
  };

  return (
    <div className="fixed z-50 bg-white border rounded-md shadow-lg" style={{ width: '300px' }}>
      <div className="flex flex-col">
        {/* Search box at top */}
        <div className="sticky top-0 bg-white p-2 border-b">
          <input
            type="text"
            placeholder="Search..."
            className="w-full px-2 py-1 text-sm border rounded text-gray-900 font-normal"
          />
        </div>
        
        {/* Select/Deselect All */}
        <div className="sticky top-[52px] bg-white p-2 border-b">
          <label className="flex items-center gap-2 p-1">
            <input
              type="checkbox"
              checked={tempSelected.length === options.length}
              onChange={handleSelectAll}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-900 font-normal">Select All</span>
          </label>
        </div>

        {/* Options list with height of 15 items then scroll */}
        <div className="p-2" style={{ maxHeight: 'calc(24px * 10)', overflowY: 'auto' }}>
          {options.map((option, index) => (
            <label key={index} className="flex items-center gap-2 p-1 hover:bg-gray-50" style={{ height: '24px' }}>
              <input
                type="checkbox"
                checked={tempSelected.includes(option)}
                onChange={() => handleTempSelect(option)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-900 font-normal">{option}</span>
            </label>
          ))}
        </div>

        {/* Clear and Apply buttons */}
        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Filter Icon component
const FilterIcon = ({ active, onClick }) => (
  <button 
    onClick={onClick}
    className={`p-1 hover:bg-gray-100 rounded ${active ? 'text-red-600' : 'text-gray-400'}`}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  </button>
);

// Update the RangeFilterDropdown component
const RangeFilterDropdown = ({ isOpen, onApply, onClose, currentRange }) => {
  const [minValue, setMinValue] = useState(currentRange?.min || '');
  const [maxValue, setMaxValue] = useState(currentRange?.max || '');

  if (!isOpen) return null;

  const handleApply = () => {
    const min = minValue === '' ? null : Number(minValue);
    const max = maxValue === '' ? null : Number(maxValue);
    onApply({ min, max });
    onClose();
  };

  const handleClear = () => {
    // Clear the current values
    setMinValue('');
    setMaxValue('');
    // Apply the cleared values
    onApply({ min: null, max: null });
    onClose();
  };

  const formatValue = (value) => {
    if (value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="fixed z-50 bg-white border rounded-md shadow-lg p-4" style={{ width: '300px' }}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-600">Minimum Value</label>
          <input
            type="number"
            value={minValue}
            onChange={(e) => setMinValue(e.target.value)}
            placeholder="Min Amount"
            className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
          />
          {minValue && (
            <div className="text-xs text-gray-500">
              {formatValue(minValue)}
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-600">Maximum Value</label>
          <input
            type="number"
            value={maxValue}
            onChange={(e) => setMaxValue(e.target.value)}
            placeholder="Max Amount"
            className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
          />
          {maxValue && (
            <div className="text-xs text-gray-500">
              {formatValue(maxValue)}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={handleClear}
            className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// Update the helper function at the top level
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Update the DateRangeFilterDropdown component
const DateRangeFilterDropdown = ({ isOpen, onApply, onClose, currentRange }) => {
  const [startDate, setStartDate] = useState(currentRange?.start || '');
  const [endDate, setEndDate] = useState(currentRange?.end || '');

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ start: startDate, end: endDate });
    onClose();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    onApply({ start: null, end: null });
    onClose();
  };

  // Helper function to convert date to yyyy-mm-dd format for input
  const formatForDateInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function to handle date change
  const handleDateChange = (value, setDate) => {
    if (!value) {
      setDate('');
      return;
    }
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      setDate(date.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="fixed z-50 bg-white border rounded-md shadow-lg" 
      style={{ 
        width: '300px',
        left: '83%',
      }}
    >
      <div className="flex flex-col">
        <div className="p-2" style={{ maxHeight: 'calc(24px * 10)', overflowY: 'auto' }}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Start Date</label>
              <input
                type="date"
                value={formatForDateInput(startDate)}
                onChange={(e) => handleDateChange(e.target.value, setStartDate)}
                className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500"
              />
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">End Date</label>
              <input
                type="date"
                value={formatForDateInput(endDate)}
                onChange={(e) => handleDateChange(e.target.value, setEndDate)}
                className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>

        {/* Clear and Apply buttons */}
        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update the formatDate function
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export function ManageProject() {
  // Move all state declarations to the top
  const [projects, setProjects] = useState(() => {
    const savedProjects = localStorage.getItem('projectsList');
    return savedProjects ? JSON.parse(savedProjects) : [];
  });
  
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => 
    parseInt(localStorage.getItem('currentPage')) || 1
  );
  const [searchTerm, setSearchTerm] = useState(() => 
    localStorage.getItem('searchTerm') || ''
  );
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSort = localStorage.getItem('sortConfig');
    return savedSort ? JSON.parse(savedSort) : { key: 'Name', direction: 'asc' };
  });
  const [selectedDepartment, setSelectedDepartment] = useState(() => 
    localStorage.getItem('selectedDepartment') || 'Engineered Fill'
  );
  const [selectedRowId, setSelectedRowId] = useState(() => 
    localStorage.getItem('selectedRowId') || null
  );
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  
  // Add these state declarations before they're used
  const [columnFilterValues, setColumnFilterValues] = useState({
    division: [],
    scope: [],
    branch: [],
    projectStatus: [],
    projectManager: [],
    billingProgress: []
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const savedFilters = localStorage.getItem('columnFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      'Project__r.Name': '',
      'dv_Division_New__c': '',
      'dv_Type__c': '',
      'Company__c': '',
      'manager': '',
      'Project__r.Project_Status__c': '',
      'Billing_Progress__c': '',
      'Next_Onsite_Date__c': ''
    };
  });

  const [amountRange, setAmountRange] = useState(() => {
    const savedRange = localStorage.getItem('amountRange');
    return savedRange ? JSON.parse(savedRange) : { min: null, max: null };
  });

  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = localStorage.getItem('dateRange');
    return savedDateRange ? JSON.parse(savedDateRange) : { start: null, end: null };
  });

  const navigate = useNavigate();

  // Fetch projects only if they're not already in state
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://projects-dev.nettlescs.com:5000/api/salesforce/all-opportunities');
        const data = await response.json();
        const filteredProjects = data.records.filter(project => project.Project__r !== null);
        setProjects(filteredProjects);
        localStorage.setItem('projectsList', JSON.stringify(filteredProjects));
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if projects array is empty
    if (projects.length === 0) {
      fetchProjects();
    } else {
      setLoading(false);
    }
  }, [projects.length]);

  // Update the sorting function to handle Project_System_Wide_ID__c
  const sortedProjects = React.useMemo(() => {
    let sortedItems = [...projects];
    if (sortConfig.key) {
      sortedItems.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'Project__r.Name') {
          aValue = a.Project__r?.Name || '';
          bValue = b.Project__r?.Name || '';
        } else if (sortConfig.key === 'Project__r.Project_System_Wide_ID__c') {
          aValue = a.Project__r?.Project_System_Wide_ID__c || '';
          bValue = b.Project__r?.Project_System_Wide_ID__c || '';
        } else if (sortConfig.key === 'Project__r.Project_Status__c') {
          aValue = a.Project__r?.Project_Status__c || '';
          bValue = b.Project__r?.Project_Status__c || '';
        } else if (sortConfig.key === 'Amount') {
          // Handle Amount as numeric values
          aValue = parseFloat(a[sortConfig.key]) || 0;
          bValue = parseFloat(b[sortConfig.key]) || 0;
        } else if (sortConfig.key === 'next_onsite') {
          // Handle next onsite date sorting
          aValue = a.next_onsite ? new Date(a.next_onsite) : new Date(0);
          bValue = b.next_onsite ? new Date(b.next_onsite) : new Date(0);
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }

        // Case insensitive comparison for strings
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        }
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      });
    }
    return sortedItems;
  }, [projects, sortConfig]);

  // Update the filteredProjects logic
  const filteredProjects = React.useMemo(() => {
    // First filter out Material Sales
    return sortedProjects
      .filter(project => project.dv_Division_New__c !== 'Material Sales')
      .filter(project => {
        // Apply column filters
        if (columnFilterValues.division.length > 0 && 
            !columnFilterValues.division.includes(project.dv_Division_New__c)) {
          return false;
        }
        if (columnFilterValues.scope.length > 0 && 
            !columnFilterValues.scope.includes(project.dv_Type__c)) {
          return false;
        }
        if (columnFilterValues.branch.length > 0 && 
            !columnFilterValues.branch.includes(project.Company__c)) {
          return false;
        }
        if (columnFilterValues.projectStatus.length > 0 && 
            !columnFilterValues.projectStatus.includes(project.Status)) {
          return false;
        }
        if (columnFilterValues.projectManager.length > 0 && 
            !columnFilterValues.projectManager.includes(project.manager)) {
          return false;
        }

        // Add amount range filter with proper number conversion
        const projectAmount = parseFloat(project.Amount) || 0;
        if (amountRange.min !== null && projectAmount < amountRange.min) {
          return false;
        }
        if (amountRange.max !== null && projectAmount > amountRange.max) {
          return false;
        }

        // Add billing progress filter check
        if (columnFilterValues.billingProgress.length > 0 && 
            !columnFilterValues.billingProgress.includes(project.BillingProgress?.toString())) {
          return false;
        }

        // Update date range filter to use next_onsite
        if (dateRange.start || dateRange.end) {
          const projectDate = project.next_onsite;
          if (!projectDate) return false;

          const date = new Date(projectDate);
          if (dateRange.start && new Date(dateRange.start) > date) return false;
          if (dateRange.end && new Date(dateRange.end) < date) return false;
        }

        // Check column filters first
        const projectName = project.Project__r?.Name || '';
        if (columnFilters['Project__r.Name'] && 
            !projectName.toLowerCase().includes(columnFilters['Project__r.Name'].toLowerCase())) {
          return false;
        }

        // Then check global search term
        if (searchTerm) {
          const searchableValues = [
            project.Opportunity_System_Wide_ID__c || '',
            projectName,
            project.dv_Type__c || '',
            project.Company__c || '',
            project.Project__r?.Project_System_Wide_ID__c || '',
            project.dv_Division_New__c || ''
          ].map(value => value.toString().toLowerCase());

          const searchTermLower = searchTerm.toLowerCase();
          return searchableValues.some(value => value.includes(searchTermLower));
        }

        return true;
      });
  }, [sortedProjects, columnFilterValues, columnFilters, amountRange, searchTerm, dateRange]);

  // Pagination
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);

  // Handle sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Update function to fetch project details with new API endpoint and response structure
  const handleProjectSelect = async (id) => {
    try {
      // First API call for opportunity details
      const opportunityResponse = await fetch(`https://projects-dev.nettlescs.com:5000/api/salesforce/opportunity/${id}`);
      const opportunityData = await opportunityResponse.json();
      const projectRecord = opportunityData.records[0];

      // Second API call for project info using Project__c
      const projectInfoResponse = await fetch(`https://projects-dev.nettlescs.com:5000/api/salesforce/projects/${projectRecord.Project__c}/Info`);
      const projectInfoData = await projectInfoResponse.json();
      const projectInfo = projectInfoData.records[0];

      // Store all project details including project ID and division
      const projectDetails = {
        company: projectRecord.Company__c,
        projectNo: projectRecord.Project__r?.Project_System_Wide_ID__c,
        opportunityNo: projectRecord.Opportunity_System_Wide_ID__c,
        projectName: projectRecord.Project__r?.Name,
        scope: projectRecord.dv_Type__c,
        deliveryMethod: projectRecord.Delivery_Method__c,
        scopeDescription: projectRecord.Project_Scope__c,
        
        // Add project info details
        projectStatus: projectInfo.Project_Status__c || '-',
        billingDate: projectInfo.gmb_Earliest_Bid_Date__c || '-',
        
        // Store address components separately
        street: projectInfo.Address?.Street || '',
        city: projectInfo.Address?.City || '',
        state: projectInfo.Address?.State || '',
        zipCode: projectInfo.Address?.ZipCode || '',
        
        // Keep the original address object for reference
        Address: projectInfo.Address,
        
        // Also keep the formatted address for backward compatibility
        projectAddress: formatAddress(projectInfo.Address),
        
        customerName: projectRecord.Contract_With__r?.Name || '-',
        
        // Add opportunity name for contacts modal
        opportunityName: projectRecord.Name,
        opportunityUUID: projectRecord.Opportunity_System_Wide_ID__c,
        
        // Add these fields for financial data
        projectId: projectRecord.Project__r?.Project_System_Wide_ID__c,
        division: projectRecord.dv_Division_New__c,
        
        // Add the Salesforce Project ID for API calls
        salesforceProjectId: projectRecord.Project__c
      };
      
      localStorage.setItem('selectedProjectDetails', JSON.stringify(projectDetails));

      // Dispatch event with project details
      const event = new CustomEvent('projectDetailsChanged', {
        detail: projectDetails
      });
      window.dispatchEvent(event);

      navigate('/dashboard');
    } catch (error) {
      console.error('Error fetching project details:', error);
      setLoading(false);
    }
  };

  // Helper function to format address for backward compatibility
  const formatAddress = (address) => {
    if (!address) return '-';
    
    const parts = [];
    if (address.Street) parts.push(address.Street);
    if (address.City) parts.push(address.City);
    if (address.State) parts.push(address.State);
    if (address.ZipCode) parts.push(address.ZipCode);
    
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  // Update handleRowClick to include navigation
  const handleRowClick = async (project) => {
    const department = project.dv_Division_New__c || 'Engineered Fill';
    setSelectedDepartment(department);
    setSelectedRowId(project.Id);
    localStorage.setItem('selectedDepartment', department);
    localStorage.setItem('isProjectSelected', 'true');
    localStorage.setItem('selectedRowId', project.Id);
    window.dispatchEvent(new CustomEvent('departmentChanged', { detail: department }));
    
    // Fetch and store detailed project data
    await handleProjectSelect(project.Id);
  };

  // Update the columns configuration
  const columns = [
    { key: 'Project__r.Name', label: 'Project Name', filterable: false, width: '20%' },
    { key: 'dv_Division_New__c', label: 'Division', filterable: true, filterKey: 'division', width: '10%' },
    { key: 'dv_Type__c', label: 'Scope', filterable: true, filterKey: 'scope', width: '15%' },
    { key: 'Amount', label: 'Amount', isRangeFilter: true, width: '10%' },
    { key: 'Company__c', label: 'Branch', filterable: true, filterKey: 'branch', width: '10%' },
    { key: 'manager', label: 'Project Manager', filterable: true, filterKey: 'projectManager', width: '10%' },
    { key: 'Status', label: 'Status', filterable: true, filterKey: 'projectStatus', width: '10%' },
    { key: 'BillingProgress', label: 'Billing Progress', filterable: true, filterKey: 'billingProgress', width: '10%' },
    { key: 'next_onsite', label: 'Next Onsite', isDateFilter: true, width: '10%' }
  ];

  // Update clearStoredData function with new order
  const clearStoredData = () => {
    // Clear all stored data
    localStorage.removeItem('projectsList');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('searchTerm');
    localStorage.removeItem('sortConfig');
    localStorage.removeItem('selectedRowId');
    localStorage.removeItem('selectedProjectDetails');
    localStorage.removeItem('isProjectSelected');
    localStorage.removeItem('columnFilters');
    localStorage.removeItem('amountRange');
    localStorage.removeItem('dateRange');
    
    // Reset column filters state with new order
    setColumnFilters({
      'Project__r.Name': '',
      'dv_Division_New__c': '',
      'dv_Type__c': '',
      'Company__c': '',
      'manager': '',
      'Project__r.Project_Status__c': '',
      'Billing_Progress__c': '',
      'Next_Onsite_Date__c': ''
    });
    
    // Dispatch event to notify dashboard
    window.dispatchEvent(new CustomEvent('projectDataCleared'));
    
    window.location.reload();
  };

  // Handle column filter change
  const handleColumnFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Add this function to handle clearing individual filters
  const clearColumnFilter = (key) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: ''
    }));
  };

  // Add handlers for the filters
  const toggleColumnFilter = (columnName, event) => {
    event.stopPropagation();
    setActiveFilterColumn(activeFilterColumn === columnName ? null : columnName);
  };

  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues(prev => ({
      ...prev,
      [columnName]: prev[columnName].includes(value)
        ? prev[columnName].filter(v => v !== value)
        : [...prev[columnName], value]
    }));
  };

  // Update the getUniqueValues function to exclude Material Sales from filters
  const getUniqueValues = (data, key, currentFilters) => {
    // First filter out Material Sales from the data
    const dataWithoutMaterialSales = data.filter(project => 
      project.dv_Division_New__c !== 'Material Sales'
    );
    
    // First filter the data based on current filter selections
    const filteredData = dataWithoutMaterialSales.filter(project => {
      // Check division filter
      if (currentFilters.division.length > 0 && 
          !currentFilters.division.includes(project.dv_Division_New__c)) {
        return false;
      }
      // Check scope filter
      if (currentFilters.scope.length > 0 && 
          !currentFilters.scope.includes(project.dv_Type__c)) {
        return false;
      }
      // Check branch filter
      if (currentFilters.branch.length > 0 && 
          !currentFilters.branch.includes(project.Company__c)) {
        return false;
      }
      // Check project status filter
      if (currentFilters.projectStatus.length > 0 && 
          !currentFilters.projectStatus.includes(project.Status)) {
        return false;
      }
      // Check project manager filter
      if (currentFilters.projectManager.length > 0 && 
          !currentFilters.projectManager.includes(project.manager)) {
        return false;
      }
      // Add billing progress filter check
      if (currentFilters.billingProgress.length > 0 && 
          !currentFilters.billingProgress.includes(project.BillingProgress?.toString())) {
        return false;
      }
      return true;
    });

    // Then get unique values from the filtered data
    if (key === 'Company__c') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-')
        .sort();
    }
    
    // Update Project Status handling to use dynamic values
    if (key === 'Status') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-')
        .sort();
    }

    // Handle project manager values
    if (key === 'manager') {
      return [...new Set(filteredData.map(item => item[key]))]
        .filter(value => value && value !== '-')
        .sort();
    }
    
    // Handle BillingProgress values
    if (key === 'BillingProgress') {
      return [...new Set(filteredData.map(item => item[key]?.toString()))]
        .filter(value => value && value !== '-')
        .sort((a, b) => parseFloat(a) - parseFloat(b)); // Sort numerically
    }
    
    return [...new Set(filteredData.map(item => {
      if (key.includes('Project__r.')) {
        const nestedKey = key.split('.')[1];
        return item.Project__r?.[nestedKey];
      }
      return item[key];
    }))]
      .filter(Boolean)
      .sort();
  };

  return (
    <div className="flex flex-col w-full">
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="flex items-center gap-4 mb-6 px-6">
          <form className="flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-stone-50 rounded-[31px] text-zinc-500 w-[30%]">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              id="projectSearch"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
          <div className="flex-grow"></div>
          
          <div className="relative group mr-[2%]">
            <svg 
              className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium text-red-700 hover:text-gray-500 cursor-pointer w-6 h-6 transition-colors duration-200" 
              focusable="false" 
              aria-hidden="true" 
              viewBox="0 0 24 24" 
              onClick={clearStoredData}
            >
              <path 
                d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9m-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8z" 
                fill="currentColor" 
              />
            </svg>
            <span className="absolute top-1/2 left-full -translate-y-1/2 ml-2 w-max px-2 py-1 bg-white text-red-700 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              Reset
            </span>
          </div>
        </div>

        {/* Table container with proper scrolling */}
        <div className="flex-1">
          <div className="min-w-full">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th 
                      key={column.key}
                      style={{ width: column.width }}
                      className="px-6 py-3"
                    >
                      <div className="flex flex-col gap-2">
                        <div 
                          className="flex items-center justify-center text-sm font-medium text-gray-500 cursor-pointer whitespace-nowrap relative group"
                          onClick={() => requestSort(column.key)}
                        >
                          <span className="truncate" title={column.label}>{column.label}</span>
                          <span className="ml-1 inline-block w-4">
                            {sortConfig.key === column.key ? 
                              (sortConfig.direction === 'asc' ? '↓' : '↑') : 
                              '↕'}
                          </span>
                          {/* Add tooltip for truncated text */}
                          <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-normal -translate-y-full top-0 left-1/2 transform -translate-x-1/2">
                            {column.label}
                          </div>
                        </div>
                        <div className="relative">
                          {column.isRangeFilter ? (
                            // Range filter for Amount column
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveFilterColumn(activeFilterColumn === 'amount' ? null : 'amount');
                                }}
                                className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${
                                  amountRange.min !== null || amountRange.max !== null ? 'text-red-600' : 'text-zinc-500'
                                }`}
                              >
                                <span>Filter</span>
                                <FiFilter className="ml-2" />
                              </button>
                              <RangeFilterDropdown
                                isOpen={activeFilterColumn === 'amount'}
                                onApply={setAmountRange}
                                onClose={() => setActiveFilterColumn(null)}
                                currentRange={amountRange}
                              />
                            </div>
                          ) : column.filterable ? (
                            // Existing dropdown filter for filterable columns
                            <div className="relative">
                              <button
                                onClick={(e) => toggleColumnFilter(column.filterKey, e)}
                                className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${
                                  columnFilterValues[column.filterKey]?.length > 0 ? 'text-red-600' : 'text-zinc-500'
                                }`}
                              >
                                <span>
                                  {columnFilterValues[column.filterKey]?.length > 0 
                                    ? `${columnFilterValues[column.filterKey].length} selected` 
                                    : 'Filter'}
                                </span>
                                <FiFilter className="ml-2" />
                              </button>
                              <ColumnFilterDropdown
                                isOpen={activeFilterColumn === column.filterKey}
                                options={getUniqueValues(projects, column.key, columnFilterValues)}
                                selectedValues={columnFilterValues[column.filterKey] || []}
                                onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                onClose={() => setActiveFilterColumn(null)}
                              />
                            </div>
                          ) : column.isDateFilter ? (
                            <div className="relative" style={{ position: 'relative' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveFilterColumn(activeFilterColumn === 'date' ? null : 'date');
                                }}
                                className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${
                                  dateRange.start || dateRange.end ? 'text-red-600' : 'text-zinc-500'
                                }`}
                              >
                                <span className="truncate">
                                  {dateRange.start || dateRange.end ? (
                                    <span className="text-xs">
                                      {dateRange.start ? formatDateForDisplay(dateRange.start) : 'Any'} 
                                      {' - '} 
                                      {dateRange.end ? formatDateForDisplay(dateRange.end) : 'Any'}
                                    </span>
                                  ) : (
                                    'Filter'
                                  )}
                                </span>
                                <FiFilter className="ml-2 shrink-0" />
                              </button>
                              <DateRangeFilterDropdown
                                isOpen={activeFilterColumn === 'date'}
                                onApply={setDateRange}
                                onClose={() => setActiveFilterColumn(null)}
                                currentRange={dateRange}
                              />
                            </div>
                          ) : (
                            // Regular search input for non-filterable columns
                            <input
                              type="text"
                              placeholder="Search"
                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                              value={columnFilters[column.key] || ''}
                              onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                            />
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
                        <span className="ml-2">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">No projects found</td>
                  </tr>
                ) : (
                  currentItems.map((project, index) => (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 cursor-pointer ${
                        project.Id === selectedRowId ? 'bg-red-50' : ''
                      }`}
                      onClick={() => handleRowClick(project)}
                    >
                      <TooltipCell 
                        content={project.Project__r?.Name}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.dv_Division_New__c}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.dv_Type__c || '-'}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.Amount ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(parseFloat(project.Amount)) : '-'}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.Company__c || '-'}
                        className="text-gray-900"
                        align="left"
                      >
                        {project.Company__c || '-'}
                      </TooltipCell>
                      <TooltipCell 
                        content={project.manager || '-'}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.Status || '-'}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={project.BillingProgress ? `${project.BillingProgress}%` : '-'}
                        className="text-gray-900"
                        align="left"
                      />
                      <TooltipCell 
                        content={formatDate(project.next_onsite)}
                        className="text-gray-900"
                        align="left"
                      />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination section */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-between items-center p-6 border-t">
            <div className="text-sm text-gray-700">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredProjects.length)} of {filteredProjects.length} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 